// src/services/projectMinutesService.js
import { fetchWithAuth } from '../utils/fetchWithAuth';

/* =========================
   Listar minutas del proyecto
   ========================= */
export const listProjectMinutes = async (projectId, filters = {}) => {
  const params = new URLSearchParams();
  if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.append('dateTo', filters.dateTo);
  
  const queryString = params.toString();
  const url = `/projects/${encodeURIComponent(projectId)}/minutes${queryString ? `?${queryString}` : ''}`;
  
  const res = await fetchWithAuth(url, { method: 'GET' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

/* =========================
   Subir minuta (PDF + meta)
   ========================= */
// payload: { file, title?, date? }
export const uploadProjectMinute = async (projectId, payload) => {
  const fd = new FormData();
  if (!payload?.file) throw new Error('Archivo PDF requerido');
  fd.append('file', payload.file);
  if (payload.title) fd.append('title', payload.title);
  if (payload.date) fd.append('date', payload.date);

  const res = await fetchWithAuth(
    `/projects/${encodeURIComponent(projectId)}/minutes`,
    { method: 'POST', body: fd }
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message || `HTTP ${res.status}`);
  }
  return res.json();
};

/* =========================
   Eliminar minuta
   ========================= */
export const deleteProjectMinute = async (projectId, minuteId) => {
  const res = await fetchWithAuth(
    `/projects/${encodeURIComponent(projectId)}/minutes/${encodeURIComponent(minuteId)}`,
    { method: 'DELETE' }
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.message || `HTTP ${res.status}`);
  }
  return res.json(); // tu backend devuelve la lista actualizada
};

/* =========================
   Actualizar minuta
   =========================
   - Primero actualiza metadatos (title/date) vía PATCH /.../:mid (JSON)
   - Si viene file, intenta PATCH /.../:mid/file (FormData)
     y si responde 404, cae a PATCH /.../:mid (FormData) (endpoint combinado)
*/
export const updateProjectMinute = async (projectId, minuteId, payload = {}) => {
  const pid = encodeURIComponent(projectId);
  const mid = encodeURIComponent(minuteId);

  // 1) Actualizar metadatos si vienen
  const hasMeta =
    Object.prototype.hasOwnProperty.call(payload, 'title') ||
    Object.prototype.hasOwnProperty.call(payload, 'date');

  if (hasMeta) {
    const meta = {
      ...(Object.prototype.hasOwnProperty.call(payload, 'title') ? { title: payload.title ?? '' } : {}),
      ...(Object.prototype.hasOwnProperty.call(payload, 'date')  ? { date:  payload.date  ?? '' } : {}),
    };

    const metaRes = await fetchWithAuth(`/projects/${pid}/minutes/${mid}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(meta),
    });

    if (!metaRes.ok) {
      const err = await metaRes.json().catch(() => ({}));
      throw new Error(err?.message || `HTTP ${metaRes.status}`);
    }
  }

  // 2) Si viene nuevo archivo, reemplazar PDF
  if (payload.file) {
    const fd = new FormData();
    fd.append('file', payload.file);
    // opcionales (algunos backends los usan para nombrado/carpeta)
    if (payload.title != null) fd.append('title', String(payload.title));
    if (payload.date  != null) fd.append('date', String(payload.date));

    // 2a) Endpoint dedicado de archivo
    let res = await fetchWithAuth(`/projects/${pid}/minutes/${mid}/file`, {
      method: 'PATCH',
      body: fd,
    });

    // 2b) Fallback: endpoint combinado (mismo path sin /file)
    if (res.status === 404) {
      res = await fetchWithAuth(`/projects/${pid}/minutes/${mid}`, {
        method: 'PATCH',
        body: fd,
      });
    }

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.message || `HTTP ${res.status}`);
    }
  }

  // No es necesario devolver nada en particular (tu UI refresca la lista)
  return true;
};

export default {
  listProjectMinutes,
  uploadProjectMinute,
  deleteProjectMinute,
  updateProjectMinute,
};
